import { useCallback, useEffect, useRef, useState } from "react";

function readPrefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

interface UseFirstItemCelebrationOptions {
  itemCount: number;
  enabled?: boolean;
  getPrefersReducedMotion?: () => boolean;
}

interface UseFirstItemCelebrationResult {
  isCelebrating: boolean;
  stopCelebrating: () => void;
}

export function useFirstItemCelebration({
  itemCount,
  enabled = true,
  getPrefersReducedMotion = readPrefersReducedMotion,
}: UseFirstItemCelebrationOptions): UseFirstItemCelebrationResult {
  const previousCountRef = useRef<number | null>(null);
  const [isCelebrating, setIsCelebrating] = useState(false);

  useEffect(() => {
    // Track the count even while disabled, so re-enabling does not mistake a
    // stale value for a fresh 0 -> n transition.
    const previousCount = previousCountRef.current;
    previousCountRef.current = itemCount;

    if (!enabled) {
      return;
    }

    // Callers must mount this only after data has loaded, or the initial
    // 0 -> n hydration would be mistaken for a creation.
    if (previousCount === 0 && itemCount > 0 && !getPrefersReducedMotion()) {
      setIsCelebrating(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount, enabled]);

  const stopCelebrating = useCallback(() => setIsCelebrating(false), []);

  return { isCelebrating, stopCelebrating };
}
